
import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { jwtDecode } from 'jwt-decode';
import ReactDOM from 'react-dom';  // Import ReactDOM for portals
import Snackbar from '../Components/Snackbar';
import ConfirmDialog from '../Components/ConfirmDialog';

const Response = ({ onRequestUpdate }) => {
  // State management
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [updating, setUpdating] = useState(null);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showReasonModal, setShowReasonModal] = useState(false);
  const [reason, setReason] = useState('');
  const [pendingAction, setPendingAction] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [responseAttachments, setResponseAttachments] = useState([]);

  // Snackbar state
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    type: 'info'
  });

  // Confirm Dialog state
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    title: '',
    message: '',
    type: 'warning',
    confirmText: 'Confirm',
    cancelText: 'Cancel',
    onConfirm: null
  });

  const [dialogLoading, setDialogLoading] = useState(false);

  // API configuration
  const API_BASE_URL = 'https://greenyellow-nightingale-241896.hostingersite.com/api';
  const authToken = localStorage.getItem('authToken')?.replace(/['\"]/g, '').trim();

  // Enhanced getCompanyId function
  const getCompanyId = useCallback(async () => {
    console.group('[DEBUG] getCompanyId() - Starting company ID resolution');

    const token = localStorage.getItem('authToken');
    console.log('📋 Token from localStorage:', token ? `${token.substring(0, 50)}...` : 'null');

    if (token) {
      try {
        console.log(' Attempting to decode JWT token...');
        const decodedToken = jwtDecode(token);
        console.log('📊 Decoded token structure:', JSON.stringify(decodedToken, null, 2));

        if (decodedToken) {
          console.log('Available token fields:', Object.keys(decodedToken));
          console.log('Token companyId field:', decodedToken.companyId);
          console.log('Token userId field:', decodedToken.userId);
          console.log('†Token id field:', decodedToken.id);
          console.log('Ž­ Token role field:', decodedToken.role);
        }

        // If role is company, use id as companyId
        if (decodedToken && decodedToken.role === 'company') {
          if (decodedToken.id) {
            console.log('✅ Role is company, using id as companyId:', decodedToken.id);
            console.groupEnd();
            return decodedToken.id;
          }
        }

        // Otherwise, use companyId field if available
        if (decodedToken && decodedToken.companyId) {
          console.log('✅ Using companyId from token:', decodedToken.companyId);
          console.groupEnd();
          return decodedToken.companyId;
        }

        // Do NOT fetch companyId from employee profile anymore
        console.warn('⚠️ No companyId found in token and skipping employee profile fetch');

      } catch (error) {
        console.error('❌ JWT decoding failed:', error);
        console.warn('„ Trying alternative methods...');
      }
    } else {
      console.warn('⚠️ No authToken found in localStorage');
      console.log('📋 All localStorage keys:', Object.keys(localStorage));
    }

    // Fallback: try localStorage
    const storedCompanyId = localStorage.getItem('companyId');
    if (storedCompanyId) {
      console.log('✅ Found companyId in localStorage:', storedCompanyId);
      console.groupEnd();
      return storedCompanyId;
    } else {
      console.warn('⚠️ No companyId found in localStorage');
    }

    console.error('❌ Unable to resolve company ID from any source');
    console.groupEnd();
    return null;
  }, []);


  // Fetch companies for name mapping with enhanced debugging
  const fetchCompanies = useCallback(async () => {
    try {
      console.group('DEBUG: fetchCompanies - DETAILED');
      const token = authToken;

      console.log('1. Token check:', token ? 'Present' : 'Missing');

      if (!token) {
        console.error('❌ Missing token for companies fetch');
        console.groupEnd();
        return;
      }

      console.log('2. API URL:', `${API_BASE_URL}/companies`);
      console.log('3. Making fetch request...');

      const response = await fetch(`${API_BASE_URL}/companies`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        credentials: 'include',
      });

      console.log('4. Response status:', response.status);
      console.log('5. Response ok:', response.ok);

      if (response.ok) {
        const data = await response.json();
        console.log('6. Raw response data:', data);
        console.log('7. Data type:', Array.isArray(data) ? 'Array' : typeof data);

        // Handle different response structures
        let companyList = [];

        if (Array.isArray(data)) {
          companyList = data;
          console.log('8. Data is direct array');
        } else if (data.data && Array.isArray(data.data)) {
          companyList = data.data;
          console.log('8. Data is in data.data property');
        } else if (data.companies && Array.isArray(data.companies)) {
          companyList = data.companies;
          console.log('8. Data is in data.companies property');
        } else {
          console.warn('8. ⚠️ Unexpected data structure:', Object.keys(data));
        }

        console.log('9. Processed companies count:', companyList.length);

        // Log all companies for debugging
        if (companyList.length > 0) {
          console.log('10. All companies:');
          companyList.forEach((comp, idx) => {
            console.log(`   Company ${idx + 1}:`, {
              id: comp._id || comp.id,
              businessName: comp.businessName,
              name: comp.name,
              email: comp.businessEmail || comp.email
            });
          });
        } else {
          console.warn('10. ⚠️ No companies found in response');
        }

        setCompanies(companyList);
        console.log('11. ✅ Companies state updated');
      } else {
        console.error('❌ Companies fetch failed with status:', response.status);
        const errorText = await response.text();
        console.error('Error response text:', errorText);
      }

      console.groupEnd();
    } catch (error) {
      console.error('❌ Error fetching companies:', error);
      console.error('Error details:', {
        message: error.message,
        stack: error.stack
      });
      console.groupEnd();
    }
  }, [API_BASE_URL, authToken]);


  // Updated getCurrentUserId function
  const getCurrentUserId = useCallback(() => {
    console.group('[DEBUG] getCurrentUserId()');

    // First, try to extract from JWT token to get the correct ID based on role
    try {
      const token = localStorage.getItem('authToken');
      if (token) {
        const decoded = jwtDecode(token);
        console.log(' Decoded token:', {
          role: decoded.role,
          id: decoded.id,
          userId: decoded.userId,
          employeeId: decoded.employeeId,
          companyId: decoded.companyId
        });

        // For company role, use the id field
        if (decoded.role === 'company' && decoded.id) {
          console.log('✅ Company user - using id from JWT:', decoded.id);
          console.groupEnd();
          return decoded.id;
        }

        // For employee role, use userId or id
        if (decoded.role === 'employee' || decoded.role === 'user') {
          const empId = decoded.userId || decoded.employeeId || decoded.id;
          if (empId) {
            console.log('✅ Employee user - using id from JWT:', empId);
            console.groupEnd();
            return empId;
          }
        }

        // Fallback: try any available ID field
        const anyId = decoded.userId || decoded.employeeId || decoded.id;
        if (anyId) {
          console.log('✅ Found userId in JWT (fallback):', anyId);
          console.groupEnd();
          return anyId;
        }
      }
    } catch (error) {
      console.error('❌ Failed to extract userId from JWT:', error);
    }

    // Fallback to localStorage
    const possibleKeys = ['userId', 'user_id', 'employeeId', 'employee_id', 'id'];
    for (const key of possibleKeys) {
      const value = localStorage.getItem(key);
      if (value) {
        const userId = value.replace(/['\\\"]/g, '').trim();
        console.log(`✅ Found userId with key '${key}':`, userId);
        console.groupEnd();
        return userId;
      }
    }

    console.warn('⚠️ No user ID found in localStorage or JWT');
    console.log('📋 All localStorage keys:', Object.keys(localStorage));
    console.groupEnd();
    return null;
  }, []);

  // Enhanced status color mapping with modern gradient approach (REMOVED ACCEPTED CASE)
  const getStatusColor = useCallback((status) => {
    switch (status?.toLowerCase()) {
      case 'pending':
        return 'bg-gradient-to-r from-amber-50 to-yellow-50 text-amber-800 border-amber-200 ring-1 ring-amber-200';
      case 'rejected':
        return 'bg-gradient-to-r from-red-50 to-rose-50 text-red-800 border-red-200 ring-1 ring-red-200';
      case 'resolved':
        return 'bg-gradient-to-r from-emerald-50 to-green-50 text-emerald-800 border-emerald-200 ring-1 ring-emerald-200';
      case 'in_progress':
        return 'bg-gradient-to-r from-purple-50 to-violet-50 text-purple-800 border-purple-200 ring-1 ring-purple-200';
      default:
        return 'bg-gradient-to-r from-slate-50 to-gray-50 text-slate-800 border-slate-200 ring-1 ring-slate-200';
    }
  }, []);

  // Enhanced status icon mapping with improved visual design (REMOVED ACCEPTED CASE)
  const getStatusIcon = useCallback((status) => {
    const iconClass = "w-4 h-4";
    switch (status?.toLowerCase()) {
      case 'pending':
        return <svg className={`${iconClass} text-amber-600`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>;
      case 'rejected':
        return <svg className={`${iconClass} text-red-600`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>;
      case 'resolved':
        return <svg className={`${iconClass} text-emerald-600`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
        </svg>;
      default:
        return <svg className={`${iconClass} text-slate-600`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>;
    }
  }, []);

  // FIXED: Fetch employees for name mapping
  const fetchAllEmployees = useCallback(async () => {
    try {
      console.group('DEBUG: fetchAllEmployees');
      const companyId = await getCompanyId();
      const token = authToken;

      if (!companyId || !token) {
        console.error('Missing company ID or token for employees fetch');
        console.error('CompanyId:', companyId, 'Token:', token ? 'present' : 'missing');
        console.groupEnd();
        return;
      }

      console.log('Fetching employees for company:', companyId);

      const response = await fetch(`${API_BASE_URL}/employees/company/${companyId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${token}`,
          'x-auth-token': token,
        },
        credentials: 'include',
      });

      console.log('Employees response status:', response.status);

      if (response.ok) {
        const data = await response.json();
        console.log('Employees raw response:', JSON.stringify(data, null, 2));

        // Handle different response structures
        const empList = Array.isArray(data) ? data : (data.data || data.employees || []);
        console.log('Processed employees list:', empList.length, 'employees');

        // Log sample employee for debugging
        if (empList.length > 0) {
          console.log('Sample employee:', {
            id: empList[0]._id || empList[0].id,
            name: empList[0].teamMemberName || empList[0].name,
            email: empList[0].email
          });
        }

        setEmployees(empList);
        console.log('✅ Employees loaded successfully:', empList.length);
      } else {
        console.warn('Employees fetch failed:', response.status);
        const errorText = await response.text();
        console.error('Error response:', errorText);
      }

      console.groupEnd();
    } catch (error) {
      console.error('❌ Error fetching employees:', error);
      console.groupEnd();
    }
  }, [API_BASE_URL, authToken, getCompanyId]);


  // FIXED: Enhanced fetchRequests function
  const fetchRequests = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      console.group('[DEBUG] fetchRequests() - Enhanced version');

      const companyId = await getCompanyId();
      const currentUserId = getCurrentUserId();
      const token = authToken;

      console.log('Request data:', {
        companyId,
        currentUserId,
        hasToken: !!token,
        tokenPreview: token ? `${token.substring(0, 20)}...` : 'none'
      });

      if (!companyId || !token || !currentUserId) {
        const missingItems = [];
        if (!companyId) missingItems.push('companyId');
        if (!token) missingItems.push('authToken');
        if (!currentUserId) missingItems.push('currentUserId');

        throw new Error(`Missing required authentication data: ${missingItems.join(', ')}`);
      }

      let url = `${API_BASE_URL}/requests?companyId=${companyId}&requestTo=${currentUserId}`;

      console.log('Œ Making request to:', url);

      const headers = {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'Authorization': `Bearer ${token}`,
        'x-auth-token': token
      };

      console.log('📋 Request headers:', Object.keys(headers));

      const response = await fetch(url, {
        method: 'GET',
        headers: headers,
        credentials: 'include'
      });

      console.log('¥ Response details:', {
        status: response.status,
        ok: response.ok,
        statusText: response.statusText,
        headers: Object.fromEntries([...response.headers.entries()])
      });

      if (!response.ok) {
        let errorText = '';
        try {
          errorText = await response.text();
          console.error('❌ Error response body:', errorText);
        } catch (e) {
          console.error('❌ Could not read error response');
        }

        throw new Error(`Failed to fetch requests (${response.status}): ${errorText || response.statusText}`);
      }

      const data = await response.json();
      console.log('📊 Raw API response:', JSON.stringify(data, null, 2));

      // Handle different response structures
      let requestsList = [];
      if (Array.isArray(data)) {
        requestsList = data;
      } else if (data.data) {
        requestsList = Array.isArray(data.data) ? data.data : [data.data];
      } else if (data.requests) {
        requestsList = Array.isArray(data.requests) ? data.requests : [data.requests];
      } else {
        requestsList = [data];
      }

      console.log('📦 Processed requests:', requestsList.length, 'items');

      // IMPROVED: Better filtering with logging - Show requests TO user AND FROM user
      console.log('Filtering requests for currentUserId:', currentUserId);
      console.log('All requests before filtering:', requestsList.map(r => ({
        id: r._id,
        subject: r.subject,
        requestTo: r.requestTo,
        requestFrom: r.requestFrom,
        status: r.status
      })));

      const myRequests = requestsList.filter(request => {
        if (!request) {
          console.log('⚠️ Null request found, skipping');
          return false;
        }

        const requestToId = String(request.requestTo || '');
        const requestFromId = String(request.requestFrom || '');
        const currentUserIdStr = String(currentUserId);

        // Show requests where user is EITHER the sender OR receiver
        const isRequestTo = requestToId === currentUserIdStr;
        const isRequestFrom = requestFromId === currentUserIdStr;
        const isMatch = isRequestTo || isRequestFrom;

        console.log(`📋 Request ${request._id}:`, {
          subject: request.subject,
          requestTo: requestToId,
          requestFrom: requestFromId,
          currentUser: currentUserIdStr,
          status: request.status,
          isRequestTo,
          isRequestFrom,
          match: isMatch
        });

        return isMatch;
      });

      console.log('✅ Filtered requests:', myRequests.length, 'items for current user');
      console.log('✅ Filtered requests by status:', {
        pending: myRequests.filter(r => r.status === 'pending').length,
        resolved: myRequests.filter(r => r.status === 'resolved').length,
        rejected: myRequests.filter(r => r.status === 'rejected').length
      });
      console.log('✅ Filtered requests by direction:', {
        sentToMe: myRequests.filter(r => String(r.requestTo) === String(currentUserId)).length,
        sentByMe: myRequests.filter(r => String(r.requestFrom) === String(currentUserId)).length
      });

      if (myRequests.length > 0) {
        console.log('📋 First request sample:', JSON.stringify(myRequests[0], null, 2));

        // NORMALIZE ATTACHMENTS: Handle various backend response structures
        console.group('Ž ATTACHMENT NORMALIZATION');
        myRequests.forEach((req, idx) => {
          // Check multiple possible locations for attachment data
          let attachmentData = [];

          // Option 1: Single file field (Cloudinary URL string) - MOST COMMON
          if (req.file && typeof req.file === 'string') {
            // Backend returns file URL directly as a string
            attachmentData = [{
              url: req.file,
              originalname: req.file.split('/').pop(), // Extract filename from URL
              mimetype: req.file.includes('/image/') ? 'image/jpeg' : 'application/octet-stream'
            }];
            console.log(`Request ${idx + 1}: Found file URL string (${req.file})`);
          }
          // Option 2: Standard attachments array
          else if (req.attachments && Array.isArray(req.attachments)) {
            attachmentData = req.attachments;
            console.log(`Request ${idx + 1}: Found attachments array (${attachmentData.length} items)`);
          }
          // Option 3: File field as object
          else if (req.file && typeof req.file === 'object') {
            attachmentData = [req.file];
            console.log(`Request ${idx + 1}: Found single file object`);
          }
          // Option 4: Multiple files field
          else if (req.files) {
            if (Array.isArray(req.files)) {
              attachmentData = req.files;
            } else if (typeof req.files === 'object') {
              // Handle multer's files object structure: { fieldname: [files] }
              attachmentData = Object.values(req.files).flat();
            }
            console.log(`Request ${idx + 1}: Found files field (${attachmentData.length} items)`);
          }
          // Option 5: attachment field (singular)
          else if (req.attachment) {
            attachmentData = Array.isArray(req.attachment) ? req.attachment : [req.attachment];
            console.log(`Request ${idx + 1}: Found attachment field`);
          }

          // Normalize the attachment data structure
          if (attachmentData.length > 0) {
            req.attachments = attachmentData.map((att, attIdx) => {
              console.log(`  Raw attachment ${attIdx + 1} data:`, att);

              // Try to get URL from various fields - check if it's already a full URL or relative path
              let fileUrl = att.url || att.path || att.location || att.filepath || att.key || att.destination;

              // If we got a path/destination, it might need the filename appended
              if (fileUrl && att.filename && !fileUrl.includes(att.filename)) {
                fileUrl = `${fileUrl}/${att.filename}`.replace(/\/+/g, '/');
                console.log(`  — Combined path+filename: ${fileUrl}`);
              }

              // If no URL but we have a filename, try to construct one
              if (!fileUrl && (att.filename || att.name || att.originalname)) {
                const fileName = att.filename || att.name || att.originalname;
                // Try common upload paths - backend usually saves to 'uploads' folder
                fileUrl = `uploads/${fileName}`;
                console.log(`  ⚠️ No URL found, constructed: ${fileUrl}`);
              }

              const normalized = {
                originalName: att.originalName || att.originalname || att.name || att.filename || `Attachment ${attIdx + 1}`,
                url: fileUrl,
                size: att.size || att.filesize || 0,
                type: att.mimetype || att.type || att.contentType || 'application/octet-stream',
                filename: att.filename || att.originalname || att.name,
                _raw: att // Keep raw data for debugging
              };

              console.log(`  📄 Normalized attachment ${attIdx + 1}:`, normalized);
              return normalized;
            });
          } else {
            req.attachments = [];
            console.log(`Request ${idx + 1}: No attachments found`);
          }
        });
        console.groupEnd();
      }

      setRequests(myRequests);
      console.groupEnd();

    } catch (error) {
      console.error('❌ Error fetching requests:', error);
      console.error('📋 Error details:', {
        name: error.name,
        message: error.message,
        stack: error.stack
      });

      setError(error.message);
      setSnackbar({
        open: true,
        message: `Failed to load requests: ${error.message}`,
        type: 'error'
      });
      console.groupEnd();
    } finally {
      setLoading(false);
    }
  }, [API_BASE_URL, authToken, getCompanyId, getCurrentUserId]);

  // ENHANCED: Updated updateRequestStatus function with resolvedAt tracking and file upload
  const updateRequestStatus = useCallback(async (requestId, status, responseReason = '', attachments = []) => {
    try {
      setUpdating(requestId);
      console.group(`[DEBUG] updateRequestStatus(${requestId}, ${status})`);

      const token = authToken;
      if (!token) {
        throw new Error('No authentication token found');
      }

      const currentTime = new Date().toISOString();
      const url = `${API_BASE_URL}/requests/${requestId}`;
      console.log('Œ Making PUT request to:', url);
      console.log('Ž Attachments:', attachments.length);

      let response;

      // If there are attachments, use FormData
      if (attachments && attachments.length > 0) {
        const formData = new FormData();
        formData.append('status', status);
        formData.append('reason', responseReason);
        formData.append('respondedAt', currentTime);
        if (status === 'resolved') {
          formData.append('resolvedAt', currentTime);
        }

        // Add files
        attachments.forEach((attachment) => {
          if (attachment.file) {
            formData.append('file', attachment.file);
          }
        });

        console.log(' Sending FormData with', attachments.length, 'file(s)');

        response = await fetch(url, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'x-auth-token': token
          },
          credentials: 'include',
          body: formData
        });
      } else {
        // No attachments, use JSON
        const updateData = {
          status: status,
          reason: responseReason,
          respondedAt: currentTime,
          ...(status === 'resolved' && { resolvedAt: currentTime })
        };

        console.log(' Update data (JSON):', JSON.stringify(updateData, null, 2));

        response = await fetch(url, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'Authorization': `Bearer ${token}`,
            'x-auth-token': token
          },
          credentials: 'include',
          body: JSON.stringify(updateData)
        });
      }

      console.log('¥ Update response status:', response.status);

      if (!response.ok) {
        let errorData = '';
        try {
          errorData = await response.text();
          console.error('❌ Update error response:', errorData);
        } catch (e) {
          console.error('❌ Could not read error response');
        }
        throw new Error(`Failed to update request status (${response.status}): ${errorData}`);
      }

      const result = await response.json();
      console.log('📊 Update result:', JSON.stringify(result, null, 2));

      // Check what field contains the response attachment
      if (result.request || result.data) {
        const updatedRequest = result.request || result.data;
        console.log('Checking response attachment fields:');
        console.log('  - responseFile:', updatedRequest.responseFile);
        console.log('  - file:', updatedRequest.file);
        console.log('  - responseAttachment:', updatedRequest.responseAttachment);
        console.log('  - attachments:', updatedRequest.attachments);
      }

      // Update the requests list - refetch to get updated data with attachment
      await fetchRequests();

      // Trigger notification count refresh in parent component
      if (onRequestUpdate && typeof onRequestUpdate === 'function') {
        onRequestUpdate();
      }

      console.log('✅ Request updated successfully');

      setSnackbar({
        open: true,
        message: `Request ${status} successfully!`,
        type: 'success'
      });

      // Close modal and reset states
      setShowReasonModal(false);
      setReason('');
      setPendingAction(null);
      setSelectedRequest(null);
      setResponseAttachments([]);

      console.groupEnd();

    } catch (error) {
      console.error('❌ Error updating request:', error);
      setError(error.message);
      setSnackbar({
        open: true,
        message: 'Failed to update request: ' + error.message,
        type: 'error'
      });
      console.groupEnd();
    } finally {
      setUpdating(null);
    }
  }, [API_BASE_URL, authToken]);

  // Handle action button clicks
  const handleAction = useCallback((request, action) => {
    setSelectedRequest(request);
    setPendingAction(action);
    setShowReasonModal(true);
    setResponseAttachments([]); // Reset attachments
  }, []);

  // Handle file upload for response
  const handleResponseFileUpload = useCallback((e) => {
    const files = Array.from(e.target.files);
    const maxSize = 2 * 1024 * 1024; // 2MB
    const validFiles = [];
    const invalidFiles = [];

    files.forEach(file => {
      if (file.size > maxSize) {
        invalidFiles.push({
          name: file.name,
          size: (file.size / (1024 * 1024)).toFixed(2) + ' MB'
        });
      } else {
        validFiles.push(file);
      }
    });

    if (invalidFiles.length > 0) {
      const errorMsg = invalidFiles.map(f => `${f.name} (${f.size}) exceeds 2MB limit`).join('\n');
      if (window.toast) {
        window.toast.error(errorMsg);
      } else {
        alert('File size errors:\n' + errorMsg);
      }
    }

    if (validFiles.length > 0) {
      const newAttachments = validFiles.map(file => ({
        id: Date.now() + Math.random(),
        name: file.name,
        size: file.size,
        file: file,
        url: URL.createObjectURL(file)
      }));

      setResponseAttachments(prev => [...prev, ...newAttachments]);

      if (window.toast) {
        window.toast.success(`${validFiles.length} file(s) added`);
      }
    }

    // Reset input
    e.target.value = '';
  }, []);

  // Remove attachment
  const handleRemoveAttachment = useCallback((attachmentId) => {
    setResponseAttachments(prev => {
      const attachment = prev.find(att => att.id === attachmentId);
      if (attachment && attachment.url) {
        URL.revokeObjectURL(attachment.url);
      }
      return prev.filter(att => att.id !== attachmentId);
    });
  }, []);

  // Format file size helper
  const formatFileSizeHelper = useCallback((bytes) => {
    if (!bytes || bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }, []);


  // Handle reason submission with validation
  const handleReasonSubmit = useCallback(() => {
    // Validate reason for rejection
    if (pendingAction === 'rejected' && (!reason || reason.trim() === '')) {
      // Show error if no reason provided for rejection
      setSnackbar({
        open: true,
        message: 'Please provide a reason for rejecting this request',
        type: 'error'
      });
      return;
    }

    // Proceed with the action if validation passes
    if (selectedRequest && pendingAction) {
      updateRequestStatus(selectedRequest._id, pendingAction, reason.trim(), responseAttachments);
    }
  }, [selectedRequest, pendingAction, reason, responseAttachments, updateRequestStatus]);


  // Component to display user/company name - OPTIMIZED
  const DisplayName = React.memo(({ userRef }) => {
    const [displayName, setDisplayName] = useState('Loading...');
    const [isLoading, setIsLoading] = useState(true);

    // Memoize the ID to prevent unnecessary fetches
    const userId = useMemo(() => {
      if (!userRef) return null;
      if (typeof userRef === 'string') return userRef;
      if (typeof userRef === 'object' && userRef !== null) {
        return userRef._id || userRef.id;
      }
      return null;
    }, [userRef]);

    useEffect(() => {
      const fetchName = async () => {
        setIsLoading(true);

        console.log('DisplayName - Starting fetch for:', userId);
        console.log('DisplayName - Employees count:', employees.length);
        console.log('DisplayName - Companies count:', companies.length);

        if (!userRef) {
          setDisplayName('Unknown User');
          setIsLoading(false);
          return;
        }

        // Check if already populated with name
        if (typeof userRef === 'object' && userRef !== null) {
          const name = userRef.teamMemberName ||
            userRef.businessName ||
            `${userRef.firstName || ''} ${userRef.lastName || ''}`.trim() ||
            userRef.name ||
            userRef.fullName ||
            userRef.displayName;
          if (name) {
            console.log('✅ DisplayName - Found name in userRef:', name);
            setDisplayName(name);
            setIsLoading(false);
            return;
          }
        }

        if (!userId) {
          setDisplayName('Unknown User');
          setIsLoading(false);
          return;
        }

        console.log('DisplayName - Searching for userId:', userId);

        // Check employees first
        const employee = employees.find(emp => {
          const empId = typeof emp === 'object' ? (emp._id || emp.id) : emp;
          const match = String(empId) === String(userId);
          if (match) {
            console.log('✅ DisplayName - Found matching employee:', emp);
          }
          return match;
        });

        if (employee) {
          const empName = employee.teamMemberName ||
            `${employee.firstName || ''} ${employee.lastName || ''}`.trim() ||
            employee.name ||
            employee.fullName ||
            employee.displayName;
          if (empName) {
            console.log('✅ DisplayName - Using employee name:', empName);
            setDisplayName(empName);
            setIsLoading(false);
            return;
          }
        }

        // Check if the userId matches the company of any of our loaded employees
        const firstEmployeeWithCompany = employees.find(emp => emp.company && emp.company._id);
        if (firstEmployeeWithCompany && String(firstEmployeeWithCompany.company._id) === String(userId)) {
          const compName = firstEmployeeWithCompany.company.businessName || firstEmployeeWithCompany.company.name;
          if (compName) {
            console.log('✅ DisplayName - Found matching company name from employees list:', compName);
            setDisplayName(compName);
            setIsLoading(false);
            return;
          }
        }

        console.log('DisplayName - Not found in employees, checking companies...');

        // Check companies array
        const company = companies.find(comp => {
          const compId = typeof comp === 'object' ? (comp._id || comp.id) : comp;
          const match = String(compId) === String(userId);
          if (match) {
            console.log('✅ DisplayName - Found matching company:', comp);
          }
          return match;
        });

        if (company) {
          const compName = company.businessName || company.name;
          if (compName) {
            console.log('✅ DisplayName - Using company name:', compName);
            setDisplayName(compName);
            setIsLoading(false);
            return;
          }
        }

        console.log('DisplayName - Not found in cached data, fetching from API...');

        // If not in cached data, fetch employee or company data directly from API
        try {
          const token = authToken;
          let resolvedName = null;

          // Try fetching from employees endpoint first
          try {
            const empResponse = await fetch(`${API_BASE_URL}/employees/${userId}`, {
              method: 'GET',
              headers: {
                'Authorization': `Bearer ${token}`,
                'x-auth-token': token,
                'Content-Type': 'application/json',
                'Accept': 'application/json'
              },
              credentials: 'include'
            });
            if (empResponse.ok) {
              const empData = await empResponse.json();
              console.log('✅ DisplayName - Fetched employee data:', empData);
              const actualEmp = empData.data || empData;
              resolvedName = actualEmp.teamMemberName ||
                `${actualEmp.firstName || ''} ${actualEmp.lastName || ''}`.trim() ||
                actualEmp.name ||
                actualEmp.fullName ||
                actualEmp.displayName;
            }
          } catch (e) {
            console.warn('DisplayName - Employee fetch error:', e);
          }

          if (resolvedName) {
            setDisplayName(resolvedName);
            setIsLoading(false);
            return;
          }

          // Fallback: Try fetching from companies endpoint
          const response = await fetch(`${API_BASE_URL}/companies/${userId}`, {
            method: 'GET',
            headers: {
              'Authorization': `Bearer ${token}`,
              'x-auth-token': token,
              'Content-Type': 'application/json',
              'Accept': 'application/json'
            },
            credentials: 'include'
          });

          console.log('DisplayName - API response status:', response.status);

          if (response.ok) {
            const companyData = await response.json();
            console.log('✅ DisplayName - Fetched company data:', companyData);
            const compName = companyData.businessName || companyData.name || 'Unknown Company';
            setDisplayName(compName);
          } else {
            console.warn('⚠️ DisplayName - API fetch failed, using fallback');
            setDisplayName(`User (${userId.substring(0, 8)}...)`);
          }
        } catch (error) {
          console.error('❌ DisplayName - Error fetching name:', error);
          setDisplayName(`User (${userId.substring(0, 8)}...)`);
        } finally {
          setIsLoading(false);
        }
      };

      fetchName();
    }, [userId, employees, companies, authToken, API_BASE_URL]); // Re-run when data changes

    if (isLoading) {
      return (
        <span className="inline-flex items-center space-x-1">
          <span className="animate-pulse bg-slate-200 h-4 w-20 rounded"></span>
        </span>
      );
    }

    return <span>{displayName}</span>;
  });

  DisplayName.displayName = 'DisplayName';


  // ✅ Enhanced format date function with better formatting options
  const formatDate = useCallback((dateString, includeTime = true) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      const options = {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        ...(includeTime && {
          hour: '2-digit',
          minute: '2-digit',
          hour12: true,
          timeZoneName: 'short'
        })
      };
      return date.toLocaleDateString('en-US', options);
    } catch (error) {
      console.error('Date formatting error:', error);
      return 'Invalid Date';
    }
  }, []);

  // ✅ Enhanced format relative time for better UX
  const formatRelativeTime = useCallback((dateString) => {
    if (!dateString) return 'N/A';
    try {
      const now = new Date();
      const date = new Date(dateString);
      const diffInMs = now - date;
      const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
      const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
      const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

      if (diffInMinutes < 60) {
        return diffInMinutes <= 1 ? 'Just now' : `${diffInMinutes}m ago`;
      } else if (diffInHours < 24) {
        return `${diffInHours}h ago`;
      } else if (diffInDays < 7) {
        return `${diffInDays}d ago`;
      } else {
        return formatDate(dateString, false);
      }
    } catch (error) {
      console.error('Relative time formatting error:', error);
      return 'Invalid Date';
    }
  }, [formatDate]);

  // ✅ Calculate resolution duration
  const getResolutionDuration = useCallback((createdAt, resolvedAt) => {
    if (!createdAt || !resolvedAt) return null;

    try {
      const created = new Date(createdAt);
      const resolved = new Date(resolvedAt);
      const diffInMs = resolved - created;
      const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
      const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

      if (diffInDays > 0) {
        return `${diffInDays} day${diffInDays > 1 ? 's' : ''} ${diffInHours % 24}h`;
      } else if (diffInHours > 0) {
        return `${diffInHours} hour${diffInHours > 1 ? 's' : ''}`;
      } else {
        const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
        return `${diffInMinutes} minute${diffInMinutes > 1 ? 's' : ''}`;
      }
    } catch (error) {
      console.error('Duration calculation error:', error);
      return null;
    }
  }, []);

  // Format file size
  const formatFileSize = useCallback((bytes) => {
    if (!bytes || bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }, []);

  // Download attachment
  const downloadAttachment = useCallback(async (attachmentUrl, fileName) => {
    try {
      console.group('¥ Download Attachment');
      console.log('Original URL:', attachmentUrl);
      console.log('File name:', fileName);

      // Check if it's a Cloudinary URL (public CDN - no auth needed)
      const isCloudinaryUrl = attachmentUrl && (
        attachmentUrl.includes('cloudinary.com') ||
        attachmentUrl.includes('res.cloudinary.com')
      );

      // Handle relative URLs by prepending API base URL
      let fullUrl = attachmentUrl;
      if (attachmentUrl && !attachmentUrl.startsWith('http')) {
        // Remove leading slash if present to avoid double slashes
        const cleanPath = attachmentUrl.startsWith('/') ? attachmentUrl.substring(1) : attachmentUrl;

        // Try different base URL constructions
        const baseUrl = API_BASE_URL.replace('/api', ''); // Remove /api if present
        fullUrl = `${baseUrl}/${cleanPath}`;

        console.log('Converted to full URL:', fullUrl);
        console.log('Base URL used:', baseUrl);
      }

      console.log('Fetching from:', fullUrl);
      console.log('Is Cloudinary URL:', isCloudinaryUrl);

      // For Cloudinary URLs, download directly without auth headers
      if (isCloudinaryUrl) {
        console.log('📦 Downloading from Cloudinary (direct link)');
        const link = document.createElement('a');
        link.href = fullUrl;
        link.download = fileName || 'download';
        link.target = '_blank';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        console.log('✅ File download initiated');
        console.groupEnd();

        if (window.toast) {
          window.toast.success('Download started!');
        }
        return;
      }

      // For backend URLs, use fetch with authentication
      const response = await fetch(fullUrl, {
        headers: {
          'Authorization': `Bearer ${authToken}`,
        },
        credentials: 'include',
      });

      console.log('Response status:', response.status);
      console.log('Response ok:', response.ok);

      if (!response.ok) {
        throw new Error(`Failed to download: ${response.status} ${response.statusText}`);
      }

      const blob = await response.blob();
      console.log('Blob size:', blob.size, 'bytes');
      console.log('Blob type:', blob.type);

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName || 'download';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      console.log('✅ File downloaded successfully');
      console.groupEnd();

      if (window.toast) {
        window.toast.success('File downloaded successfully!');
      }
    } catch (error) {
      console.error('❌ Error downloading file:', error);
      console.groupEnd();

      if (window.toast) {
        window.toast.error('Failed to download file: ' + error.message);
      }
    }
  }, [API_BASE_URL, authToken]);

  // Enhanced get file icon with more file types
  const getFileIcon = useCallback((fileName) => {
    if (!fileName) return null;

    const extension = fileName.split('.').pop()?.toLowerCase();
    const iconClass = "w-5 h-5";

    if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'svg', 'webp'].includes(extension)) {
      return <svg className={`${iconClass} text-blue-600`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
      </svg>;
    } else if (['mp3', 'wav', 'ogg', 'flac', 'aac'].includes(extension)) {
      return <svg className={`${iconClass} text-emerald-600`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" />
      </svg>;
    } else if (['pdf'].includes(extension)) {
      return <svg className={`${iconClass} text-red-600`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
      </svg>;
    } else if (['doc', 'docx'].includes(extension)) {
      return <svg className={`${iconClass} text-blue-700`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
      </svg>;
    } else {
      return <svg className={`${iconClass} text-slate-600`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
      </svg>;
    }
  }, []);

  // FIXED: Load data once on mount
  useEffect(() => {
    const loadData = async () => {
      try {
        console.log('📊 Starting data load sequence...');
        setLoading(true);

        // Fetch employees and companies in parallel to speed up load time and prevent blocking
        await Promise.allSettled([
          fetchAllEmployees(),
          fetchCompanies()
        ]);

        // Load requests
        await fetchRequests();
        console.log('✅ Data loading complete');
      } catch (error) {
        console.error('❌ Error in data loading sequence:', error);
        setError('Failed to load data. Please refresh the page and try again.');
        setLoading(false);
      } finally {
        setLoading(false);
      }
    };

    loadData();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // ✅ Empty dependency array - component remounts via key prop to refresh


  // Enhanced Loading state with better animation - only show full page spinner on initial empty load
  if (loading && requests.length === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <div className="relative">
            <div className="animate-spin rounded-full h-16 w-16 border-4 border-slate-200 border-t-blue-600 mx-auto mb-6"></div>
            <div className="absolute inset-0 rounded-full h-16 w-16 border-4 border-transparent border-r-blue-400 animate-pulse mx-auto"></div>
          </div>
          <h3 className="text-lg font-semibold text-slate-800 mb-2">Loading Requests</h3>
          <p className="text-slate-600 max-w-sm">Fetching your assigned requests and employee data...</p>
        </div>
      </div>
    );
  }

  // Enhanced Error state
  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 to-rose-50 flex items-center justify-center p-4">
        <div className="text-center max-w-md">
          <div className="bg-white rounded-2xl shadow-xl p-8">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h3 className="text-xl font-semibold text-slate-900 mb-3">Unable to Load Requests</h3>
            <p className="text-slate-600 mb-6 text-sm leading-relaxed">{error}</p>
            <div className="space-y-3">
              <button
                onClick={() => {
                  setError(null);
                  fetchRequests();
                }}
                className="w-full px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-medium rounded-xl hover:from-blue-700 hover:to-indigo-700 transform hover:scale-105 transition-all duration-200 shadow-lg"
              >
                Try Again
              </button>
              <p className="text-slate-400 text-xs">
                If the issue persists, please contact your system administrator
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6">

        {/* Compact Empty State */}
        {requests.filter(r => {
          const currentUserIdStr = String(getCurrentUserId());
          // Incoming tab: Only pending requests TO the user
          return r.status === 'pending' && String(r.requestTo) === currentUserIdStr;
        }).length === 0 ? (
          <div className="text-center py-12">
            <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md mx-auto">
              <div className="w-16 h-16 bg-gradient-to-br from-blue-100 to-indigo-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-slate-900 mb-2">
                No Pending Requests
              </h3>
              <p className="text-slate-600 text-sm mb-4">
                All requests have been handled or new requests will appear here when assigned to you.
              </p>
              <button
                onClick={fetchRequests}
                className="px-5 py-2.5 bg-gradient-to-r from-blue-600 to-indigo-600 text-white text-sm font-medium rounded-xl hover:from-blue-700 hover:to-indigo-700 transform hover:scale-105 transition-all duration-200 shadow-md"
              >
                „ Refresh Requests
              </button>
            </div>
          </div>
        ) : (
          // Compact Requests List - Show incoming pending requests
          <div className="space-y-5">
            {(() => {
              const currentUserIdStr = String(getCurrentUserId());
              console.log('[DISPLAY FILTER] Current User ID:', currentUserIdStr);
              console.log('[DISPLAY FILTER] Total requests:', requests.length);

              const filtered = requests.filter(request => {
                const isSentToMe = String(request.requestTo) === currentUserIdStr;
                // Incoming tab: Only pending requests TO the user
                const match = request.status === 'pending' && isSentToMe;
                console.log(`  Request ${request._id} (${request.subject}): status=${request.status}, requestTo=${request.requestTo}, requestFrom=${request.requestFrom}, isSentToMe=${isSentToMe}, match=${match}`);
                console.log(`  Request ${request._id} requestFrom details:`, request.requestFrom);
                return match;
              });

              console.log('[DISPLAY FILTER] Filtered requests:', filtered.length);

              return filtered.map((request) => {
                const currentUserIdStr = String(getCurrentUserId());
                const isSentByMe = String(request.requestFrom) === currentUserIdStr;
                const isSentToMe = String(request.requestTo) === currentUserIdStr;

                return (
                  <div key={request._id} className="bg-white rounded-2xl shadow-lg border border-slate-200 overflow-hidden hover:shadow-xl transition-all duration-300 group">
                    <div className="p-6">
                      {/* Compact Request Header */}
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex items-start space-x-3">
                          <div className="w-11 h-11 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center shadow-md">
                            <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                            </svg>
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <h3 className="text-lg font-bold text-slate-900 group-hover:text-blue-600 transition-colors">
                                {request.subject || 'No Subject'}
                              </h3>
                            </div>
                            <div className="flex flex-wrap items-center gap-2 text-xs text-slate-600">
                              <div className="flex items-center space-x-1">
                                <span className="font-medium text-slate-500">From</span>
                                <span className="font-semibold text-slate-800">
                                  <DisplayName userRef={request.requestFrom} />
                                </span>
                              </div>


                              <span className="text-slate-300">•</span>
                              <div className="flex items-center space-x-1">
                                <span className="font-medium text-slate-500">To</span>
                                <span className="font-semibold text-slate-800">
                                  <DisplayName userRef={request.requestTo} />
                                </span>
                              </div>

                              <span className="text-slate-300">•</span>
                              <span className="flex items-center space-x-1">
                                <svg className="w-3 h-3 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                                <span>{formatRelativeTime(request.createdAt)}</span>
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className={`px-3 py-1.5 rounded-xl text-xs font-semibold flex items-center space-x-1.5 ${getStatusColor(request.status)}`}>
                          {getStatusIcon(request.status)}
                          <span className="capitalize">{request.status || 'pending'}</span>
                        </div>
                      </div>

                      {/* Compact Request Description */}
                      <div className="mb-4">
                        <h4 className="text-xs font-semibold text-slate-700 mb-2 flex items-center">
                          <svg className="w-3.5 h-3.5 mr-1.5 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                          Request Details
                        </h4>
                        <div className="bg-gradient-to-br from-slate-50 to-blue-50 rounded-xl p-4 border border-slate-100">
                          <p className="text-slate-800 leading-relaxed text-sm">
                            {request.description || 'No description provided for this request.'}
                          </p>
                        </div>
                      </div>

                      {/* Compact Date Information */}
                      <div className="mb-4">
                        <h4 className="text-xs font-semibold text-slate-700 mb-2 flex items-center">
                          <svg className="w-3.5 h-3.5 mr-1.5 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          Timeline & Status History
                        </h4>

                        <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
                          {/* Created Date */}
                          <div className="bg-slate-50 rounded-lg p-3 border border-slate-100">
                            <div className="flex items-center space-x-1.5 mb-1">
                              <svg className="w-3 h-3 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
                              </svg>
                              <span className="text-xs font-medium text-slate-600">Created</span>
                            </div>
                            <p className="text-xs text-slate-800 font-medium">{formatDate(request.createdAt)}</p>
                            <p className="text-[10px] text-slate-500 mt-0.5">{formatRelativeTime(request.createdAt)}</p>
                          </div>

                          {/* Responded Date */}
                          {request.respondedAt && (
                            <div className="bg-blue-50 rounded-lg p-3 border border-blue-100">
                              <div className="flex items-center space-x-1.5 mb-1">
                                <svg className="w-3 h-3 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                                <span className="text-xs font-medium text-blue-600">Responded</span>
                              </div>
                              <p className="text-xs text-blue-800 font-medium">{formatDate(request.respondedAt)}</p>
                              <p className="text-[10px] text-blue-500 mt-0.5">{formatRelativeTime(request.respondedAt)}</p>
                            </div>
                          )}

                          {/* Resolved Date */}
                          {request.status === 'resolved' && request.resolvedAt && (
                            <>
                              <div className="bg-emerald-50 rounded-lg p-3 border border-emerald-100">
                                <div className="flex items-center space-x-1.5 mb-1">
                                  <svg className="w-3 h-3 text-emerald-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                                  </svg>
                                  <span className="text-xs font-medium text-emerald-600">Resolved</span>
                                </div>
                                <p className="text-xs text-emerald-800 font-medium">{formatDate(request.resolvedAt)}</p>
                                <p className="text-[10px] text-emerald-500 mt-0.5">{formatRelativeTime(request.resolvedAt)}</p>
                              </div>

                              {/* Resolution Duration */}
                              <div className="bg-green-50 rounded-lg p-3 border border-green-100">
                                <div className="flex items-center space-x-1.5 mb-1">
                                  <svg className="w-3 h-3 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
                                  </svg>
                                  <span className="text-xs font-medium text-green-600">Duration</span>
                                </div>
                                <p className="text-xs text-green-800 font-medium">
                                  {getResolutionDuration(request.createdAt, request.resolvedAt) || 'N/A'}
                                </p>
                                <p className="text-[10px] text-green-500 mt-0.5">Total time</p>
                              </div>
                            </>
                          )}
                        </div>
                      </div>

                      {/* Compact Resolved Status Highlight */}
                      {request.status === 'resolved' && request.resolvedAt && (
                        <div className="mb-4">
                          <div className="bg-gradient-to-r from-emerald-50 via-green-50 to-emerald-50 border-2 border-emerald-200 rounded-xl p-4 shadow-sm">
                            <div className="flex items-center space-x-3 mb-3">
                              <div className="w-10 h-10 bg-gradient-to-br from-emerald-500 to-green-600 rounded-full flex items-center justify-center shadow-md">
                                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                                </svg>
                              </div>
                              <div className="flex-1">
                                <h4 className="text-base font-bold text-emerald-800 flex items-center">
                                  ✅ Request Successfully Resolved
                                </h4>
                                <p className="text-xs text-emerald-600 mt-0.5">
                                  This request has been completed and marked as resolved
                                </p>
                              </div>
                            </div>

                            <div className="bg-white bg-opacity-70 backdrop-blur-sm rounded-lg p-3 border border-emerald-100">
                              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                                <div className="text-center md:text-left">
                                  <div className="flex items-center justify-center md:justify-start space-x-1.5 mb-1">
                                    <svg className="w-3 h-3 text-emerald-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3a1 1 0 011-1h6a1 1 0 011 1v4h3a1 1 0 011 1v11a2 2 0 01-2 2H6a2 2 0 01-2-2V8a1 1 0 011-1h3z" />
                                    </svg>
                                    <span className="text-xs font-medium text-emerald-600">Resolved Date</span>
                                  </div>
                                  <p className="text-sm font-bold text-emerald-800">
                                    {formatDate(request.resolvedAt, false)}
                                  </p>
                                </div>

                                <div className="text-center md:text-left">
                                  <div className="flex items-center justify-center md:justify-start space-x-1.5 mb-1">
                                    <svg className="w-3 h-3 text-emerald-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                    <span className="text-xs font-medium text-emerald-600">Resolved Time</span>
                                  </div>
                                  <p className="text-sm font-bold text-emerald-800">
                                    {new Date(request.resolvedAt).toLocaleTimeString('en-US', {
                                      hour: '2-digit',
                                      minute: '2-digit',
                                      hour12: true,
                                      timeZoneName: 'short'
                                    })}
                                  </p>
                                </div>

                                <div className="text-center md:text-left">
                                  <div className="flex items-center justify-center md:justify-start space-x-1.5 mb-1">
                                    <svg className="w-3 h-3 text-emerald-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
                                    </svg>
                                    <span className="text-xs font-medium text-emerald-600">Time Since</span>
                                  </div>
                                  <p className="text-sm font-bold text-emerald-800">
                                    {formatRelativeTime(request.resolvedAt)}
                                  </p>
                                </div>
                              </div>

                              <div className="mt-3 pt-3 border-t border-emerald-200">
                                <p className="text-center text-[10px] text-emerald-600 font-medium">
                                  🎉 Resolution completed successfully • Thank you for your response
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Compact Rejected Status Highlight */}
                      {request.status === 'rejected' && (
                        <div className="mb-4">
                          <div className="bg-gradient-to-r from-red-50 via-rose-50 to-red-50 border-2 border-red-200 rounded-xl p-4 shadow-sm">
                            <div className="flex items-center space-x-3 mb-3">
                              <div className="w-10 h-10 bg-gradient-to-br from-red-500 to-rose-600 rounded-full flex items-center justify-center shadow-md">
                                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                                </svg>
                              </div>
                              <div className="flex-1">
                                <h4 className="text-base font-bold text-red-800 flex items-center">
                                  ❌ Request Rejected
                                </h4>
                                <p className="text-xs text-red-600 mt-0.5">
                                  This request has been reviewed and rejected
                                </p>
                              </div>
                            </div>

                            <div className="bg-white bg-opacity-70 backdrop-blur-sm rounded-lg p-3 border border-red-100">
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                <div className="text-center md:text-left">
                                  <div className="flex items-center justify-center md:justify-start space-x-1.5 mb-1">
                                    <svg className="w-3 h-3 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                    <span className="text-xs font-medium text-red-600">Rejected Date</span>
                                  </div>
                                  <p className="text-sm font-bold text-red-800">
                                    {request.respondedAt ? formatDate(request.respondedAt, false) : 'N/A'}
                                  </p>
                                </div>

                                <div className="text-center md:text-left">
                                  <div className="flex items-center justify-center md:justify-start space-x-1.5 mb-1">
                                    <svg className="w-3 h-3 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                    <span className="text-xs font-medium text-red-600">Time Since</span>
                                  </div>
                                  <p className="text-sm font-bold text-red-800">
                                    {request.respondedAt ? formatRelativeTime(request.respondedAt) : 'N/A'}
                                  </p>
                                </div>
                              </div>

                              <div className="mt-3 pt-3 border-t border-red-200">
                                <p className="text-center text-[10px] text-red-600 font-medium">
                                  Review the reason below for more details
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Compact Attachments Section */}
                      {request.attachments && request.attachments.length > 0 && (
                        <div className="mb-4">
                          <h4 className="text-xs font-semibold text-slate-700 mb-2 flex items-center">
                            <svg className="w-3.5 h-3.5 mr-1.5 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                            </svg>
                            Attachments ({request.attachments.length})
                          </h4>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                            {request.attachments.map((attachment, index) => {
                              // Debug: Log the entire attachment object
                              console.log('Attachment data for display:', attachment);

                              // Attachments are already normalized in fetchRequests
                              const fileName = attachment.originalName || attachment.filename || attachment.name || `Attachment ${index + 1}`;
                              let fileUrl = attachment.url;
                              const fileSize = attachment.size;

                              // If no URL, try to construct from _raw data or filename
                              if (!fileUrl && attachment._raw) {
                                console.log('🔧 No URL found, checking _raw data:', attachment._raw);

                                // Check all possible URL fields in raw data
                                fileUrl = attachment._raw.url ||
                                  attachment._raw.path ||
                                  attachment._raw.location ||
                                  attachment._raw.filepath ||
                                  attachment._raw.key ||
                                  attachment._raw.filename;

                                console.log('— Extracted URL from _raw:', fileUrl);
                              }

                              // If still no URL but we have a filename, construct a URL
                              if (!fileUrl && (attachment.filename || attachment._raw?.filename)) {
                                const fn = attachment.filename || attachment._raw?.filename;
                                fileUrl = `uploads/requests/${fn}`;
                                console.log('—ï Constructed URL from filename:', fileUrl);
                              }

                              console.log('✅ Final URL for download:', fileUrl);

                              return (
                                <div key={index} className="flex items-center justify-between p-3 bg-slate-50 rounded-xl border border-slate-100 hover:bg-slate-100 transition-colors group/attachment">
                                  <div className="flex items-center space-x-3">
                                    <div className="w-8 h-8 bg-white rounded-lg flex items-center justify-center shadow-sm border border-slate-200">
                                      {getFileIcon(fileName)}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <p className="font-medium text-slate-900 text-xs truncate" title={fileName}>
                                        {fileName}
                                      </p>
                                      {fileSize > 0 && (
                                        <p className="text-[10px] text-slate-500 mt-0.5">{formatFileSize(fileSize)}</p>
                                      )}
                                    </div>
                                  </div>
                                  <button
                                    onClick={() => {
                                      console.log('–ï Download clicked for:', fileName, 'URL:', fileUrl);
                                      downloadAttachment(fileUrl, fileName);
                                    }}
                                    className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                                    title={fileUrl ? "Download file" : "URL not available"}
                                    disabled={!fileUrl}
                                  >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                    </svg>
                                  </button>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      {/* Compact Response Reason Section */}
                      {request.reason && (
                        <div className="mb-4">
                          <h4 className="text-xs font-semibold text-slate-700 mb-2 flex items-center">
                            <svg className="w-3.5 h-3.5 mr-1.5 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" />
                            </svg>
                            Response Note
                          </h4>
                          <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-4">
                            <p className="text-blue-900 leading-relaxed text-sm">{request.reason}</p>
                          </div>
                        </div>
                      )}

                      {/* Compact Action Buttons - Only show for pending requests TO the user */}
                      {request.status === 'pending' && isSentToMe && (
                        <div className="flex items-center space-x-3">
                          <button
                            onClick={() => handleAction(request, 'rejected')}
                            disabled={updating === request._id}
                            className="px-4 py-2 bg-gradient-to-r from-red-600 to-rose-600 text-white text-sm font-medium rounded-xl hover:from-red-700 hover:to-rose-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-1.5 transition-all duration-200 shadow-md hover:shadow-lg transform hover:scale-105"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                            </svg>
                            <span>Reject</span>
                          </button>
                          <button
                            onClick={() => handleAction(request, 'resolved')}
                            disabled={updating === request._id}
                            className="px-4 py-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white text-sm font-medium rounded-xl hover:from-blue-700 hover:to-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-1.5 transition-all duration-200 shadow-md hover:shadow-lg transform hover:scale-105"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <span>Resolve</span>
                          </button>
                          {updating === request._id && (
                            <div className="flex items-center space-x-2 text-slate-600">
                              <div className="animate-spin rounded-full h-5 w-5 border-2 border-slate-300 border-t-slate-600"></div>
                              <span className="text-xs font-medium">Processing...</span>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                );
              });
            })()}
          </div>
        )}

        {/* Compact Reason Modal */}
        {showReasonModal &&
          ReactDOM.createPortal(
            <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-[999999] p-4">
              <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full transform transition-all duration-300 scale-100">
                <div className="p-6">
                  {/* Header */}
                  <div className="text-center mb-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-blue-100 to-indigo-100 rounded-xl flex items-center justify-center mx-auto mb-3">
                      {pendingAction === 'rejected' ? (
                        <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      ) : (
                        <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      )}
                    </div>
                    <h3 className="text-lg font-semibold text-slate-900 mb-1">
                      {pendingAction === 'rejected' ? 'Reject Request' : 'Resolve Request'}
                    </h3>
                    <p className="text-slate-600 text-xs">
                      {pendingAction === 'rejected'
                        ? 'Please provide a reason for rejection (required)'
                        : 'Provide a reason or note for this action (optional)'}
                    </p>
                  </div>

                  {/* Reason Input */}
                  <div className="mb-4">
                    <label className="block text-xs font-medium text-slate-700 mb-2">
                      Reason or Note
                      {pendingAction === 'rejected' && (
                        <span className="text-red-500 ml-1">*</span>
                      )}
                    </label>
                    <textarea
                      value={reason}
                      onChange={(e) => setReason(e.target.value)}
                      placeholder={`Enter your reason for ${pendingAction === 'rejected' ? 'rejecting' : 'resolving'} this request...`}
                      rows={4}
                      className={`w-full px-3 py-2 border rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none placeholder-slate-400 transition-colors ${pendingAction === 'rejected' && (!reason || reason.trim() === '')
                        ? 'border-red-300 focus:ring-red-500'
                        : 'border-slate-300'
                        }`}
                      autoFocus
                    />
                    {pendingAction === 'rejected' && (!reason || reason.trim() === '') && (
                      <p className="text-xs text-red-500 mt-1 flex items-center">
                        <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        Reason is required for rejection
                      </p>
                    )}
                    {reason && reason.trim().length > 0 && (
                      <p className="text-xs text-slate-500 mt-1">
                        {reason.trim().length} characters
                      </p>
                    )}
                  </div>

                  {/* Attachments Section */}
                  <div className="mb-4">
                    <div className="flex items-center mb-3">
                      <svg className="w-5 h-5 text-purple-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                      </svg>
                      <h3 className="text-base font-semibold text-slate-800">Attachments (Optional)</h3>
                    </div>

                    {/* File Type Cards */}
                    <div className="grid grid-cols-3 gap-3 mb-4">
                      {/* Images Card */}
                      <div className="relative">
                        <input
                          type="file"
                          id="responseImageInput"
                          multiple
                          onChange={handleResponseFileUpload}
                          className="hidden"
                          accept="image/*"
                        />
                        <label
                          htmlFor="responseImageInput"
                          className="flex flex-col items-center p-4 bg-blue-50 border-2 border-blue-200 rounded-2xl hover:bg-blue-100 hover:border-blue-300 transition-all cursor-pointer"
                        >
                          <div className="w-12 h-12 bg-blue-500 rounded-xl flex items-center justify-center mb-2">
                            <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                          </div>
                          <span className="text-xs font-medium text-blue-700">Images</span>
                          <span className="text-xs text-blue-600 mt-1">Max 2 MB</span>
                        </label>
                      </div>

                      {/* Files Card */}
                      <div className="relative">
                        <input
                          type="file"
                          id="responseFileInput"
                          multiple
                          onChange={handleResponseFileUpload}
                          className="hidden"
                          accept=".pdf,.doc,.docx,.txt"
                        />
                        <label
                          htmlFor="responseFileInput"
                          className="flex flex-col items-center p-4 bg-purple-50 border-2 border-purple-200 rounded-2xl hover:bg-purple-100 hover:border-purple-300 transition-all cursor-pointer"
                        >
                          <div className="w-12 h-12 bg-purple-500 rounded-xl flex items-center justify-center mb-2">
                            <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                          </div>
                          <span className="text-xs font-medium text-purple-700">Files</span>
                          <span className="text-xs text-purple-600 mt-1">Max 2 MB</span>
                        </label>
                      </div>

                      {/* Audio Files Card */}
                      <div className="relative">
                        <input
                          type="file"
                          id="responseAudioInput"
                          multiple
                          onChange={handleResponseFileUpload}
                          className="hidden"
                          accept="audio/*"
                        />
                        <label
                          htmlFor="responseAudioInput"
                          className="flex flex-col items-center p-4 bg-green-50 border-2 border-green-200 rounded-2xl hover:bg-green-100 hover:border-green-300 transition-all cursor-pointer"
                        >
                          <div className="w-12 h-12 bg-green-500 rounded-xl flex items-center justify-center mb-2">
                            <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" />
                            </svg>
                          </div>
                          <span className="text-xs font-medium text-green-700">Audio Files</span>
                          <span className="text-xs text-green-600 mt-1">Max 2 MB</span>
                        </label>
                      </div>
                    </div>

                    {/* Attachments List */}
                    {responseAttachments.length > 0 && (
                      <div className="mt-3 space-y-2">
                        {responseAttachments.map((attachment) => (
                          <div
                            key={attachment.id}
                            className="flex items-center justify-between p-2 bg-slate-50 rounded-lg border border-slate-200"
                          >
                            <div className="flex items-center space-x-2 flex-1 min-w-0">
                              <svg className="w-4 h-4 text-slate-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                              </svg>
                              <div className="flex-1 min-w-0">
                                <p className="text-xs font-medium text-slate-900 truncate">{attachment.name}</p>
                                <p className="text-[10px] text-slate-500">{formatFileSizeHelper(attachment.size)}</p>
                              </div>
                            </div>
                            <button
                              type="button"
                              onClick={() => handleRemoveAttachment(attachment.id)}
                              className="p-1 text-red-500 hover:bg-red-50 rounded transition-colors flex-shrink-0"
                              title="Remove file"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                              </svg>
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Action Buttons */}
                  <div className="flex items-center justify-end space-x-3">
                    <button
                      onClick={() => {
                        setShowReasonModal(false);
                        setReason('');
                        setPendingAction(null);
                        setSelectedRequest(null);
                        setResponseAttachments([]);
                      }}
                      disabled={updating}
                      className="px-4 py-2 text-slate-700 text-sm bg-slate-100 font-medium rounded-xl hover:bg-slate-200 disabled:opacity-50 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleReasonSubmit}
                      disabled={updating || (pendingAction === 'rejected' && (!reason || reason.trim() === ''))}
                      className={`px-5 py-2 text-white text-sm font-medium rounded-xl flex items-center space-x-2 transition-all duration-200 shadow-md min-w-[110px] justify-center ${pendingAction === 'rejected'
                        ? 'bg-gradient-to-r from-red-600 to-rose-600 hover:from-red-700 hover:to-rose-700'
                        : 'bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700'
                        } disabled:opacity-50 disabled:cursor-not-allowed`}
                    >
                      {updating ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                          <span>Processing...</span>
                        </>
                      ) : (
                        <span>Confirm</span>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>,
            document.body
          )}

      </div>

      {/* Snackbar Component */}
      <Snackbar
        open={snackbar.open}
        message={snackbar.message}
        type={snackbar.type}
        duration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        position="top-right"
      />

      {/* Confirm Dialog Component */}
      <ConfirmDialog
        open={confirmDialog.open}
        title={confirmDialog.title}
        message={confirmDialog.message}
        type={confirmDialog.type}
        confirmText={confirmDialog.confirmText}
        cancelText={confirmDialog.cancelText}
        onConfirm={confirmDialog.onConfirm}
        onCancel={() => setConfirmDialog({ ...confirmDialog, open: false })}
        loading={dialogLoading}
        showIcon={true}
      />
    </div>
  );

};

export default Response;


